import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Instagram, Mail, Phone, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/galleria", label: "Galleria" },
  { to: "/sponsor", label: "Sponsor" },
  { to: "/iscrizioni", label: "Iscrizioni" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col bg-[#050505]">
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#050505]/85 backdrop-blur-xl border-b border-[#2A2A2A]"
            : "bg-transparent"
        }`}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
            aria-label="Break the Rules 3x3 — Home"
          >
            <img
              src="/LOGO%20UFFICIALE.svg"
              alt="Break the Rules 3x3"
              className="h-12 md:h-14 w-auto"
            />
            <span className="sr-only">Break the Rules 3x3</span>
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm uppercase tracking-[0.2em] font-bold font-manrope transition-colors ${
                    isActive
                      ? "text-[#FF4500]"
                      : "text-white hover:text-[#FF4500]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#FF4500]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/iscrizioni"
              data-testid="nav-cta-iscriviti"
              className="ml-4 px-6 py-3 bg-[#FF4500] text-white uppercase tracking-[0.15em] text-xs font-extrabold border-2 border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-colors duration-300"
            >
              Iscriviti
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#2A2A2A] text-white"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="md:hidden overflow-hidden bg-[#050505] border-t border-[#2A2A2A]"
            >
              <div className="flex flex-col p-6 gap-1">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `py-4 border-b border-[#1A1A1A] uppercase font-bebas text-3xl tracking-wider ${
                        isActive ? "text-[#FF4500]" : "text-white"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <Link
                  to="/iscrizioni"
                  data-testid="mobile-cta-iscriviti"
                  className="mt-6 px-6 py-4 bg-[#FF4500] text-white uppercase tracking-[0.15em] text-sm font-extrabold text-center border-2 border-[#FF4500]"
                >
                  Iscriviti Ora
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1 pt-20" data-testid="main-content">
        <Outlet />
      </main>

      <footer
        data-testid="site-footer"
        className="relative mt-24 border-t border-[#2A2A2A] bg-[#050505]"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <img
                src="/LOGO%20UFFICIALE.svg"
                alt="Break the Rules 3x3"
                className="h-24 md:h-28 w-auto"
              />
              <p className="mt-6 text-[#A3A3A3] max-w-sm leading-relaxed">
                Il torneo 3x3 più scatenato di Maserà di Padova. Quattro giorni,
                un solo campione.
              </p>
            </div>

            <div className="md:col-span-4">
              <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-5">
                Contatti
              </p>
              <ul className="space-y-4 text-white">
                <li className="flex items-start gap-3">
                  <Mail
                    size={18}
                    className="text-[#FF4500] mt-1 shrink-0"
                  />
                  <a
                    href="mailto:infobtr3x3@gmail.com"
                    data-testid="footer-email"
                    className="hover:text-[#FF4500] transition-colors break-all"
                  >
                    infobtr3x3@gmail.com
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone
                    size={18}
                    className="text-[#FF4500] mt-1 shrink-0"
                  />
                  <a
                    href="tel:+393295653110"
                    data-testid="footer-phone"
                    className="hover:text-[#FF4500] transition-colors"
                  >
                    329 5653110
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Instagram
                    size={18}
                    className="text-[#FF4500] mt-1 shrink-0"
                  />
                  <a
                    href="https://instagram.com/break_the_rules_trnmnt"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-instagram"
                    className="hover:text-[#FF4500] transition-colors"
                  >
                    @break_the_rules_trnmnt
                  </a>
                </li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-5">
                Dove
              </p>

              <a
                href="https://www.google.com/maps/search/?api=1&query=Parco+della+Libert%C3%A0%2C+Viale+dei+Kennedy%2C+Maser%C3%A0+di+Padova+%28PD%29"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-white hover:text-[#FF4500] transition-colors"
              >
                <MapPin
                  size={18}
                  className="text-[#FF4500] mt-1 shrink-0"
                />
                <p className="leading-relaxed">
                  Parco della Libertà
                  <br />
                  Viale dei Kennedy
                  <br />
                  Maserà di Padova (PD)
                </p>
              </a>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">
                Quando
              </p>
              <p className="text-white mt-2 font-bebas text-3xl tracking-wider">
                2 — 5 Luglio 2026
              </p>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-[#1A1A1A] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">
            <span>
              © {new Date().getFullYear()} Break the Rules 3x3 — Tutti i diritti
              riservati
            </span>
            <span>Made with fire by <a href="https://2mcreations.it" target="_blank" className="hover:text-[#FF4500] transition-colors">2M creations</a> with ❤️</span>
          </div>
        </div>
      </footer>
    </div>
  );
}